import React, {useEffect, useState} from 'react';
import {Grid, Typography} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {Quote} from '../../../Core/mobx/ParameterStore/Quote';
import Card from '@material-ui/core/Card';
import {NumberFormatCustom, SwitchCustom, TextBox} from '../../../Core/UI';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {Parameter, ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import MuiAlert from '@material-ui/lab/Alert';
import {FormType} from '../../PreQualification/types';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/QuoteXref';
import {shouldShowHHIWarning, WarningMessage} from '@/Agent/Common/WarningMessage';

type IULTabProps = {
    refParameters: Prequal;
    valueChanged: (p: Parameter, value: any, formType?: FormType) => void;
    parameters: Quote;
    formType: FormType;
    changeConditionalValues: (param: {name: string; value: any}, dependentParam: {name: string; value: any}) => void;
    parameterStore: ParameterStore;
};

const disabledProducts = ['Ameritas: IUL  - FLX living benefits', 'MOO - IUL Express'];

const IULTab = observer(
    ({valueChanged, parameters, refParameters, formType, changeConditionalValues, parameterStore}: IULTabProps) => {
        const rawHHI = parameterStore.prequal.incomeHouseholdText.value ?? '';
        const hhi = Number(String(rawHHI).replace(/[,$]/g, '')) || 0;
        const {onValueChanged} = parameterStore;
        const [error, setError] = useState({
            specifiedFaceAmountWinflex: '',
            fundPercent: '',
            deathBenefitChangeYear: '',
            deathBenefitChangeYearFGPathsetter: '',
            specifiedPremiumEndAgeAmeritas: '',
            specifiedPremiumEndAgeFGPathsetter: '',
        });

        const required = (value?: string) => (
            <span>
                {value && value} <span style={{color: 'red'}}>*</span>
            </span>
        );

        const specifiedPremiumAmount = Number(parameters.specifiedPremiumFGPathsetter?.value) || 0;
        const {exceeds: showPremiumWarning, threshold: premiumThreshold} = shouldShowHHIWarning({
            premium: specifiedPremiumAmount,
            annualHHI: hhi,
            premiumMode: 'Monthly',
        });

        useEffect(() => {
            onValueChanged(null, parameters.lswFlexLifeNL, false);
            onValueChanged(null, parameters.lswFlexLife, false);
        }, []);

        useEffect(() => {
            if (parameters.productIUL.value === 'FGEverlast' && parameters.specifiedPremiumFGPathsetter?.value === '') {
                valueChanged(parameters.specifiedPremiumFGPathsetter, parameterStore.prequal.monthlyBudget.value);
            }
        }, []);

        const validatespecifiedFaceAmountWinflex = () => {
            const {healthRatingAmeritasAdult, healthRatingAmeritasJuvenile, specifiedFaceAmountWinflex} = parameters;

            if (
                ['PreferredPlus', 'PreferredNonTobacco', 'SelectNonTobacco', 'PreferredTobacco'].includes(
                    healthRatingAmeritasAdult.value,
                ) ||
                healthRatingAmeritasJuvenile.value == 'Juvenile'
            ) {
                setError((prevState) => ({
                    ...prevState,
                    specifiedFaceAmountWinflex:
                        +specifiedFaceAmountWinflex.value >= 100000 ? '' : 'specifiedFaceAmountWinflex >= 100000',
                }));
            }
            if (
                ['NonTobacco', 'RapidNonTobacco', 'Tobacco', 'RapidTobacco'].includes(healthRatingAmeritasAdult.value)
            ) {
                setError((prevState) => ({
                    ...prevState,
                    specifiedFaceAmountWinflex:
                        +specifiedFaceAmountWinflex.value >= 50000 ? '' : 'specifiedFaceAmountWinflex >= 50000',
                }));
            }
        };

        const totalFundPercent =
            +parameters.fixedInterest.value +
            +parameters.sp1yr.value +
            +parameters.spAdjust.value +
            +parameters.sp2yr.value +
            +parameters.russell.value +
            +parameters.bnp1yr.value +
            +parameters.bnp2yr.value;

        const validateDeathBenefitChangeYear = () => {
            setError((prevState) => ({
                ...prevState,
                deathBenefitChangeYear:
                    parameters.deathBenefitChangeYear.value < refParameters.age.value
                        ? 'deathBenefitChangeYear > age'
                        : '',
            }));
        };

        const validateDeathBenefitChangeYearFGPathsetter = () => {
            setError((prevState) => ({
                ...prevState,
                deathBenefitChangeYearFGPathsetter:
                    parameters.deathBenefitChangeYearFGPathsetter.value < refParameters.age.value
                        ? 'deathBenefitChangeYearFGPathsetter > age'
                        : '',
            }));
        };

        const validateSpecifiedPremiumEndAgeAmeritas = () => {
            setError((prevState) => ({
                ...prevState,
                specifiedPremiumEndAgeAmeritas:
                    parameters.specifiedPremiumEndAgeAmeritas.value > refParameters.age.value &&
                    parameters.specifiedPremiumEndAgeAmeritas.value <= 120
                        ? 'specifiedPremiumEndAgeAmeritas > age AND specifiedPremiumEndAgeAmeritas <= 120'
                        : '',
            }));
        };

        const validateSpecifiedPremiumEndAgeFGPathsetter = () => {
            setError((prevState) => ({
                ...prevState,
                specifiedPremiumEndAgeAmeritas:
                    parameters.specifiedPremiumEndAgeFGPathsetter.value > refParameters.age.value &&
                    parameters.specifiedPremiumEndAgeFGPathsetter.value <= 120
                        ? 'specifiedPremiumEndAgeFGPathsetter > age AND specifiedPremiumEndAgeFGPathsetter <= 120'
                        : '',
            }));
        };

        // make this invalid for now, as a temporary reversal
        const isDisabledOptionFg = (option: string) =>
            parameters.productIUL.value === 'FGEverlast' &&
            (option === 'Premium Solve' || option === 'No Solve') &&
            false;

        return (
            <>
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Carrier Product</Typography>
                    </div>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <SelectInputElement
                                param={{
                                    label: 'Product',
                                    name: 'productIUL',
                                    options: fieldOptions.productIUL.options,
                                    optionValues: fieldOptions.productIUL.optionValues,
                                    ...parameters.productIUL,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                                isOptionDisabledCb={(option: string) => disabledProducts.includes(option)}
                            />
                            {parameters.productIUL.value === 'FGEverlast' && (
                                <small>Age 0-45: $150,000 minimum face, Age 46-80: $100,000 minimum face</small>
                            )}
                            {parameters.productIUL.value === 'FGPathsetter' && (
                                <small>Age 0-80: Minimum $50,000 face value</small>
                            )}
                        </Grid>
                    </Grid>
                </Card>
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Basics</Typography>
                    </div>
                    <Grid container spacing={3}>
                        {parameters.ameritas.value && refParameters.age.value < 18 && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Health Rating',
                                        name: 'healthRatingAmeritasJuvenile',
                                        options: fieldOptions.healthRatingAmeritasJuvenile.options,
                                        optionValues: fieldOptions.healthRatingAmeritasJuvenile.optionValues,
                                        ...parameters.healthRatingAmeritasJuvenile,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.ameritas.value && refParameters.age.value >= 18 && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Health Rating',
                                        name: 'healthRatingAmeritasAdult',
                                        options: fieldOptions.healthRatingAmeritasAdult.options,
                                        optionValues: fieldOptions.healthRatingAmeritasAdult.optionValues,
                                        ...parameters.healthRatingAmeritasAdult,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.ameritas.value &&
                            (parameters.healthRatingAmeritasAdult.value === 'NonTobacco' ||
                                parameters.healthRatingAmeritasAdult.value === 'Tobacco' ||
                                parameters.healthRatingAmeritasTerm.value === 'Standard') && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Table Rating',
                                            name: 'tableRatingAmeritas',
                                            options: fieldOptions.tableRatingAmeritas.options,
                                            optionValues: fieldOptions.tableRatingAmeritas.optionValues,
                                            ...parameters.tableRatingAmeritas,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {parameters.moo.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Risk Class',
                                        name: 'healthRatingMOOIUL',
                                        options: fieldOptions.healthRatingMOOIUL.options,
                                        optionValues: fieldOptions.healthRatingMOOIUL.optionValues,
                                        ...parameters.healthRatingMOOIUL,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.fg.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Risk Class',
                                        name: 'healthRatingFGIUL',
                                        options: fieldOptions.healthRatingFGIUL.options,
                                        optionValues: fieldOptions.healthRatingFGIUL.optionValues,
                                        ...parameters.healthRatingFGIUL,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.fg.value &&
                            ['StandardNonsmoker', 'StandardSmoker'].includes(parameters.healthRatingFGIUL.value) && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Table Rating',
                                            name: 'tableRatingFGIUL',
                                            options: fieldOptions.tableRatingFGIUL.options,
                                            optionValues: fieldOptions.tableRatingFGIUL.optionValues,
                                            ...parameters.tableRatingFGIUL,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {(parameters.lswFlexLife.value || parameters.lswFlexLifeNL.value) && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Table Rating',
                                        name: 'tableRatingLSWIUL',
                                        options: fieldOptions.tableRatingLSWIUL.options,
                                        optionValues: fieldOptions.tableRatingLSWIUL.optionValues,
                                        ...parameters.tableRatingLSWIUL,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.ameritas.value && (
                            <Grid item xs={6}>
                                <SwitchCustom
                                    param={{
                                        name: 'flatExtra',
                                        label: 'Flat Extra',
                                        ...parameters.flatExtra,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.flatExtra.value && (
                            <Grid item xs={12}>
                                <TextBox
                                    id={'flatExtraPerm'}
                                    label={'Policy Lifetime Flat Extra (per $1000)'}
                                    value={parameters.flatExtraPerm?.value}
                                    onChange={(value) => valueChanged(parameters.flatExtraPerm, value, formType)}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                    }}
                                />
                            </Grid>
                        )}
                        {parameters.flatExtra.value &&
                            (parameters.ameritas.value || parameters.northAmerican.value) && (
                                <>
                                    <Grid item xs={6}>
                                        <TextBox
                                            id={'flatExtraYears'}
                                            label={'Temporary Flat Extra Years'}
                                            value={parameters.flatExtraYears?.value}
                                            onChange={(value) =>
                                                valueChanged(parameters.flatExtraYears, value, formType)
                                            }
                                            type="number"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextBox
                                            id={'flatExtraTemp'}
                                            label={'Temporary Flat Extra Amount (per $1000)'}
                                            value={parameters.flatExtraTemp?.value}
                                            onChange={(value) =>
                                                valueChanged(parameters.flatExtraTemp, value, formType)
                                            }
                                            InputProps={{
                                                inputComponent: NumberFormatCustom,
                                            }}
                                        />
                                    </Grid>
                                </>
                            )}
                    </Grid>
                </Card>
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Coverage and Premium</Typography>
                    </div>
                    <Grid container spacing={3}>
                        {parameters.productIUL.value === 'AmeritasIULFLXlivingbenefits' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Solve Scenario',
                                        name: 'solveScenario',
                                        options: fieldOptions.solveScenario.options,
                                        ...parameters.solveScenario,
                                    }}
                                    required={required('')}
                                    valueChanged={(p: Parameter, v: any, ft?: FormType) => {
                                        const param = {name: 'solveScenario', value: v};
                                        valueChanged(p, v, ft);

                                        if (v === 'Cashflowknowncoveragepremium') {
                                            changeConditionalValues(param, {name: 'solveForIUL', value: 'Cashflow'});
                                        }

                                        if (v === 'Premiumknowncoverage' || v === 'Cashflowknowncoveragepremium') {
                                            changeConditionalValues(param, {name: 'faceAmount', value: 'Specified'});
                                        }

                                        if (v === 'Coverageknownpremium' || v === 'Cashflowknowncoveragepremium') {
                                            changeConditionalValues(param, {
                                                name: 'premiumMode',
                                                value: 'Specifiedpremium',
                                            });
                                        }

                                        if (v === 'Premiumknowncoverage') {
                                            switch (parameters.solveDetailsPremium.value) {
                                                case 'Solvefortargetpremium':
                                                    changeConditionalValues(param, {
                                                        name: 'premiumMode',
                                                        value: 'Target',
                                                    });
                                                    break;
                                                case 'SolveformaximumnonMecpremium':
                                                    changeConditionalValues(param, {
                                                        name: 'premiumMode',
                                                        value: 'MaximumnonMec',
                                                    });
                                                    break;
                                                case 'Solveforminimumpremium':
                                                    changeConditionalValues(param, {
                                                        name: 'premiumMode',
                                                        value: 'Minimum',
                                                    });
                                                    break;
                                                default:
                                                    break;
                                            }
                                        }
                                    }}
                                    formType={formType}
                                />
                                <small>setting solve Scenario will change or overwrite existing settings</small>
                            </Grid>
                        )}
                        {parameters.solveScenario.value === 'Premiumknowncoverage' &&
                            parameters.productIUL.value === 'AmeritasIULFLXlivingbenefits' && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Scenario Details',
                                            name: 'solveDetailsPremium',
                                            options: fieldOptions.solveDetailsPremium.options,
                                            ...parameters.solveDetailsPremium,
                                        }}
                                        required={required('')}
                                        valueChanged={(p: Parameter, v: any, ft?: FormType) => {
                                            const param = {name: 'solveDetailsPremium', value: v};
                                            valueChanged(p, v, ft);

                                            if (
                                                v === 'Solvefortargetpremium' ||
                                                v === 'SolveformaximumnonMecpremium' ||
                                                v === 'Solveforminimumpremium' ||
                                                parameters.solveDetailsCoverage.value === 'Solvefortargetcoverage' ||
                                                parameters.solveDetailsCoverage.value ===
                                                    'Solveforminimumcoveragemaxfunding'
                                            ) {
                                                changeConditionalValues(param, {name: 'solveForIUL', value: 'Nosolve'});
                                                changeConditionalValues(param, {
                                                    name: 'solveForIULLSW',
                                                    value: 'Nosolve',
                                                });
                                            }

                                            if (v === 'Solveforpremiumwithcashvaluetarget') {
                                                changeConditionalValues(param, {
                                                    name: 'solveForIUL',
                                                    value: 'Faceamount',
                                                });
                                                changeConditionalValues(param, {
                                                    name: 'solveForIULLSW',
                                                    value: 'Faceamount',
                                                });
                                            }
                                            if (
                                                v === 'Solvefortargetpremium' &&
                                                parameters.solveScenario.value === 'Premiumknowncoverage'
                                            ) {
                                                changeConditionalValues(param, {name: 'premiumMode', value: 'Target'});
                                            }
                                            if (
                                                v === 'SolveformaximumnonMecpremium' &&
                                                parameters.solveScenario.value === 'Premiumknowncoverage'
                                            ) {
                                                changeConditionalValues(param, {
                                                    name: 'premiumMode',
                                                    value: 'MaximumnonMec',
                                                });
                                            }
                                            if (
                                                v === 'Solveforminimumpremium' &&
                                                parameters.solveScenario.value === 'Premiumknowncoverage'
                                            ) {
                                                changeConditionalValues(param, {name: 'premiumMode', value: 'Minimum'});
                                            }
                                        }}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {parameters.solveScenario.value === 'Coverageknownpremium' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Scenario Details',
                                        name: 'solveDetailsCoverage',
                                        options: fieldOptions.solveDetailsCoverage.options,
                                        ...parameters.solveDetailsCoverage,
                                    }}
                                    required={required('')}
                                    valueChanged={(p: Parameter, v: any, ft?: FormType) => {
                                        const param = {name: 'solveDetailsCoverage', value: v};
                                        valueChanged(p, v, ft);

                                        if (
                                            v === 'Solvefortargetcoverage' ||
                                            v === 'Solveforminimumcoveragemaxfunding' ||
                                            parameters.solveDetailsPremium.value === 'Solvefortargetpremium' ||
                                            parameters.solveDetailsPremium.value === 'SolveformaximumnonMecpremium' ||
                                            parameters.solveDetailsPremium.value === 'Solveforminimumpremium'
                                        ) {
                                            changeConditionalValues(param, {name: 'solveForIUL', value: 'Nosolve'});
                                            changeConditionalValues(param, {name: 'solveForIULLSW', value: 'Nosolve'});
                                        }

                                        if (v === 'Solveforcoveragewithcashvaluetarget') {
                                            changeConditionalValues(param, {name: 'solveForIUL', value: 'Premium'});
                                            changeConditionalValues(param, {name: 'solveForIULLSW', value: 'Premium'});
                                        }

                                        if (v === 'Solvefortargetcoverage') {
                                            changeConditionalValues(param, {name: 'faceAmount', value: 'Target'});
                                        }

                                        if (v === 'Solveforminimumcoveragemaxfunding') {
                                            changeConditionalValues(param, {
                                                name: 'faceAmount',
                                                value: 'MinimumNonMec',
                                            });
                                        }
                                    }}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {((parameters.faceAmount.value === 'Specified' &&
                            parameters.productIUL.value === 'AmeritasIULFLXlivingbenefits') ||
                            (parameters.productIUL.value === 'MOOIULExpress' &&
                                parameters.easySolveOptionIULMoo.value === 'Premium') ||
                            (['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) &&
                                ['PremiumSolve', 'NoSolve'].includes(parameters.solveForIULFGPathsetter.value))) && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'specifiedFaceAmountWinflex'}
                                    label={required('Specified Face Amount')}
                                    value={parameters.specifiedFaceAmountWinflex?.value}
                                    onChange={(value) => {
                                        valueChanged(parameters.specifiedFaceAmountWinflex, value, undefined);
                                        validatespecifiedFaceAmountWinflex();
                                    }}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                    }}
                                    error={!!error.specifiedFaceAmountWinflex}
                                    helperText={error.specifiedFaceAmountWinflex}
                                />
                            </Grid>
                        )}
                        {(parameters.premiumMode.value === 'Specifiedpremium' ||
                            (parameters.easySolveOptionIULMoo.value === 'Face' && parameters.moo.value)) && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'specifiedPremium'}
                                    label={required('Specified Premium Amount')}
                                    value={parameters.specifiedPremium?.value}
                                    onChange={(value) => valueChanged(parameters.specifiedPremium, value, formType)}
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                    }}
                                />
                            </Grid>
                        )}
                        {((parameters.solveForIULFGPathsetter.value === 'FaceSolve' && parameters.fg.value) ||
                            (parameters.solveForIULFGPathsetter.value === 'NoSolve' && parameters.fg.value)) && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'specifiedPremiumFGPathsetter'}
                                    label={required('Specified Premium Amount')}
                                    value={parameters.specifiedPremiumFGPathsetter?.value}
                                    onChange={(value) =>
                                        valueChanged(parameters.specifiedPremiumFGPathsetter, value, formType)
                                    }
                                    InputProps={{
                                        inputComponent: NumberFormatCustom,
                                    }}
                                />
                                {showPremiumWarning && <WarningMessage value={premiumThreshold} />}
                            </Grid>
                        )}
                        {!['MOOIULExpress', 'FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Premium Payment Frequency',
                                        name: 'premiumFrequency',
                                        options: fieldOptions.premiumFrequency.options,
                                        optionValues: fieldOptions.premiumFrequency.optionValues,
                                        ...parameters.premiumFrequency,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}

                        {parameters.productIUL.value === 'MOOIULExpress' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Premium Payment Frequency',
                                        name: 'premiumFrequencyMOOIUL',
                                        options: fieldOptions.premiumFrequencyMonthlyOnly.options,
                                        optionValues: fieldOptions.premiumFrequencyMonthlyOnly.optionValues,
                                        ...parameters.premiumFrequencyMOOIUL,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Premium Payment Frequency',
                                        name: 'premiumFrequencyFGPathsetter',
                                        options: fieldOptions.premiumFrequencyMonthlyOnly.options,
                                        optionValues: fieldOptions.premiumFrequencyMonthlyOnly.optionValues,
                                        ...parameters.premiumFrequencyFGPathsetter,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Card>
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Coverage and Premium Details</Typography>
                    </div>
                    <Grid container spacing={3}>
                        {parameters.moo.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Solve For',
                                        name: 'solveForIULMoo',
                                        options: fieldOptions.solveForIULMoo.options,
                                        optionValues: fieldOptions.solveForIULMoo.optionValues,
                                        ...parameters.solveForIULMoo,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.moo.value && parameters.solveForIULMoo.value == 'EasySolve' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Easy Solve Option',
                                        name: 'easySolveOptionIULMoo',
                                        options: fieldOptions.easySolveOptionIULMoo.options,
                                        optionValues: fieldOptions.easySolveOptionIULMoo.optionValues,
                                        ...parameters.easySolveOptionIULMoo,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.fg.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Solve For',
                                        name: 'solveForIULFGPathsetter',
                                        options: fieldOptions.solveForIULFGPathsetter.options,
                                        optionValues: fieldOptions.solveForIULFGPathsetter.optionValues,
                                        ...parameters.solveForIULFGPathsetter,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                    isOptionDisabledCb={isDisabledOptionFg}
                                />
                            </Grid>
                        )}
                        {parameters.ameritas.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Compliance Test',
                                        name: 'complianceTest',
                                        options: fieldOptions.complianceTest.options,
                                        optionValues: fieldOptions.complianceTest.optionValues,
                                        ...parameters.complianceTest,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.complianceTest.value !== 'CashValueAccumulationTest' &&
                            !['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) && (
                                <Grid item xs={4}>
                                    <SwitchCustom
                                        param={{
                                            name: 'mecAvoidance',
                                            label: 'MEC Avoidance',
                                            ...parameters.mecAvoidance,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {!['MOOIULExpress', 'FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Death Benefit Option',
                                        name: 'deathBenefitOption',
                                        options: fieldOptions.deathBenefitOption.options,
                                        optionValues: fieldOptions.deathBenefitOption.optionValues,
                                        ...parameters.deathBenefitOption,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.productIUL.value === 'MOOIULExpress' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Death Benefit Option',
                                        name: 'deathBenefitOptionMOOIUL',
                                        options: fieldOptions.deathBenefitOptionMOOIul.options,
                                        optionValues: fieldOptions.deathBenefitOptionMOOIul.optionValues,
                                        ...parameters.deathBenefitOptionMOOIUL,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Death Benefit Option',
                                        name: 'deathBenefitOptionFGPathsetter',
                                        options: fieldOptions.deathBenefitOptionFGPathsetter.options,
                                        optionValues: fieldOptions.deathBenefitOptionFGPathsetter.optionValues,
                                        ...parameters.deathBenefitOptionFGPathsetter,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.premiumSolveOptionLSW.value !== 'MinimumPremium' &&
                            !['MOOIULExpress', 'FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) &&
                            refParameters.age.value < 70 && (
                                <Grid item xs={6}>
                                    <TextBox
                                        id={'deathBenefitChangeYear'}
                                        label={'Death Benefit Change Age'}
                                        value={parameters.deathBenefitChangeYear?.value}
                                        onChange={(value) => {
                                            valueChanged(parameters.deathBenefitChangeYear, value, formType);
                                            validateDeathBenefitChangeYear();
                                        }}
                                        type="number"
                                        error={!!error.deathBenefitChangeYear}
                                        helperText={error.deathBenefitChangeYear}
                                    />
                                </Grid>
                            )}
                        {['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) &&
                            refParameters.age.value < 66 && (
                                <Grid item xs={6}>
                                    <TextBox
                                        id={'deathBenefitChangeYearFGPathsetter'}
                                        label={'Death Benefit Change Age'}
                                        value={parameters.deathBenefitChangeYearFGPathsetter?.value}
                                        onChange={(value) => {
                                            valueChanged(
                                                parameters.deathBenefitChangeYearFGPathsetter,
                                                value,
                                                formType,
                                            );
                                            validateDeathBenefitChangeYear();
                                        }}
                                        type="number"
                                        error={!!error.deathBenefitChangeYearFGPathsetter}
                                        helperText={error.deathBenefitChangeYearFGPathsetter}
                                    />
                                </Grid>
                            )}
                        {parameters.ameritas.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Solve For',
                                        name: 'solveForIUL',
                                        options: fieldOptions.solveForIUL.options,
                                        optionValues: fieldOptions.solveForIUL.optionValues,
                                        ...parameters.solveForIUL,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {(parameters.lswFlexLifeNL.value || parameters.lswFlexLife.value) && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Solve For',
                                        name: 'solveForIULLSW',
                                        options: fieldOptions.solveForIULLSW.options,
                                        optionValues: fieldOptions.solveForIULLSW.optionValues,
                                        ...parameters.solveForIULLSW,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {parameters.solveForIUL.value !== 'Faceamount' &&
                            parameters.productIUL.value === 'AmeritasIULFLXlivingbenefits' && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Face Amount',
                                            name: 'faceAmount',
                                            options: fieldOptions.faceAmount.options,
                                            optionValues: fieldOptions.faceAmount.optionValues,
                                            ...parameters.faceAmount,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {parameters.solveForIUL.value === 'Faceamount' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Face Solve Option',
                                        name: 'faceSolveOption',
                                        options: fieldOptions.faceSolveOption.options,
                                        optionValues: fieldOptions.faceSolveOption.optionValues,
                                        ...parameters.faceSolveOption,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) &&
                            parameters.solveForIULFGPathsetter.value === 'FaceSolve' && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Face Solve Option',
                                            name: 'faceSolveOptionFGPathsetter',
                                            options: fieldOptions.faceSolveOptionFGPathsetter.options,
                                            optionValues: fieldOptions.faceSolveOptionFGPathsetter.optionValues,
                                            ...parameters.faceSolveOptionFGPathsetter,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {parameters.solveForIUL.value !== 'Premium' && parameters.ameritas.value && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Premium Mode',
                                        name: 'premiumMode',
                                        options: fieldOptions.premiumMode.options,
                                        optionValues: fieldOptions.premiumMode.optionValues,
                                        ...parameters.premiumMode,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                        {(parameters.lswFlexLifeNL.value || parameters.lswFlexLife.value) &&
                            parameters.solveForIULLSW.value !== 'Premium' && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Premium Mode',
                                            name: 'premiumModeLSW',
                                            options: fieldOptions.premiumModeLSW.options,
                                            optionValues: fieldOptions.premiumModeLSW.optionValues,
                                            ...parameters.premiumModeLSW,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {parameters.productIUL.value === 'AmeritasIULFLXlivingbenefits' &&
                            parameters.solveForIUL.value === 'Premium' && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Premium Solve Option',
                                            name: 'premiumSolveOption',
                                            options: fieldOptions.premiumSolveOption.options,
                                            ...parameters.premiumSolveOption,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {!['AmeritasIULFLXlivingbenefitis', 'MOOIULExpress', 'FGPathsetter', 'FGEverlast'].includes(
                            parameters.productIUL.value,
                        ) &&
                            parameters.solveForIULLSW.value === 'Premium' && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Premium Solve Option',
                                            name: 'premiumSolveOptionLSW',
                                            options: fieldOptions.premiumSolveOptionLSW.options,
                                            optionValues: fieldOptions.premiumSolveOptionLSW.optionValues,
                                            ...parameters.premiumSolveOptionLSW,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) &&
                            parameters.solveForIULFGPathsetter.value === 'PremiumSolve' && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Premium Solve Option',
                                            name: 'premiumSolveOptionLSW',
                                            options: fieldOptions.premiumSolveOptionFGPathsetter.options,
                                            optionValues: fieldOptions.premiumSolveOptionFGPathsetter.optionValues,
                                            ...parameters.premiumSolveOptionFGPathsetter,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {(parameters.solveForIULLSW.value !== 'Premium' ||
                            parameters.solveForIUL.value !== 'Premium') &&
                            !['MOOIULExpress', 'FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) && (
                                <Grid item xs={6}>
                                    <SelectInputElement
                                        param={{
                                            label: 'Premium Payment End Age',
                                            name: 'premiumEndAgeAmeritas',
                                            options: fieldOptions.premiumEndAgeAmeritas.options,
                                            ...parameters.premiumEndAgeAmeritas,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                        {parameters.premiumEndAgeAmeritas.value === 'specifiedage' && parameters.ameritas.value && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'specifiedPremiumEndAgeAmeritas'}
                                    label={'Premium Payment End Specified Age'}
                                    value={parameters.specifiedPremiumEndAgeAmeritas?.value}
                                    onChange={(value) => {
                                        valueChanged(parameters.specifiedPremiumEndAgeAmeritas, value, formType);
                                        validateSpecifiedPremiumEndAgeAmeritas();
                                    }}
                                    type="number"
                                    error={!!error.specifiedPremiumEndAgeAmeritas}
                                    helperText={error.specifiedPremiumEndAgeAmeritas}
                                />
                            </Grid>
                        )}
                        {['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) &&
                            ((parameters.solveForIULFGPathsetter.value === 'FaceSolve' && parameters.fg.value) ||
                                (parameters.solveForIULFGPathsetter.value === 'NoSolve' && parameters.fg.value)) && (
                                <Grid item xs={6}>
                                    <TextBox
                                        id={'specifiedPremiumEndAgeFGPathsetter'}
                                        label={'Premium Payment End Specified Age'}
                                        value={parameters.specifiedPremiumEndAgeFGPathsetter?.value}
                                        onChange={(value) => {
                                            valueChanged(
                                                parameters.specifiedPremiumEndAgeFGPathsetter,
                                                value,
                                                formType,
                                            );
                                            validateSpecifiedPremiumEndAgeFGPathsetter();
                                        }}
                                        type="number"
                                        error={!!error.specifiedPremiumEndAgeFGPathsetter}
                                        helperText={error.specifiedPremiumEndAgeFGPathsetter}
                                    />
                                </Grid>
                            )}
                        {['FGPathsetter', 'FGEverlast'].includes(parameters.productIUL.value) && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'lumpSumFGPathSetter'}
                                    label={'Lump Sum'}
                                    value={parameters.lumpSumFGPathSetter?.value}
                                    onChange={(value) => {
                                        valueChanged(parameters.lumpSumFGPathSetter, value, formType);
                                    }}
                                    type="number"
                                />
                            </Grid>
                        )}
                        {parameters.ameritas.value &&
                            (parameters.solveForIUL.value === 'Premium' ||
                                parameters.solveForIUL.value === 'Faceamount' ||
                                parameters.solveForIULLSW.value === 'Premium') && (
                                <>
                                    <Grid item xs={6}>
                                        <TextBox
                                            id={'cashValueTargetAge'}
                                            label={'cash Value Target Age'}
                                            value={parameters.cashValueTargetAge?.value}
                                            onChange={(value) =>
                                                valueChanged(parameters.cashValueTargetAge, value, formType)
                                            }
                                            type="number"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextBox
                                            id={'cashValueTargetAmount'}
                                            label={'Cash Value Target Amount'}
                                            value={parameters.cashValueTargetAmount?.value}
                                            onChange={(value) =>
                                                valueChanged(parameters.cashValueTargetAmount, value, undefined)
                                            }
                                            InputProps={{
                                                inputComponent: NumberFormatCustom,
                                            }}
                                        />
                                    </Grid>
                                </>
                            )}
                        {parameters.solveForIUL.value === 'Cashflow' && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'cashFlowStartAge'}
                                    label={'Cashflow Start Age'}
                                    value={parameters.cashFlowStartAge?.value}
                                    onChange={(value) => valueChanged(parameters.cashFlowStartAge, value, formType)}
                                    type="number"
                                />
                            </Grid>
                        )}
                        {parameters.solveForIUL.value === 'Cashflow' && (
                            <Grid item xs={6}>
                                <TextBox
                                    id={'cashFlowEndAge'}
                                    label={'Cashflow End Age'}
                                    value={parameters.cashFlowEndAge?.value}
                                    onChange={(value) => valueChanged(parameters.cashFlowEndAge, value, formType)}
                                    type="number"
                                />
                            </Grid>
                        )}
                        {parameters.solveForIUL.value === 'Cashflow' && (
                            <Grid item xs={6}>
                                <SelectInputElement
                                    param={{
                                        label: 'Cashflow Solve Option',
                                        name: 'cashFlowSolveOption',
                                        options: fieldOptions.cashFlowSolveOption.options,
                                        optionValues: fieldOptions.cashFlowSolveOption.optionValues,
                                        ...parameters.cashFlowSolveOption,
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                    formType={formType}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Card>
                {parameters.solveForIUL.value !== 'Cashflow' && (
                    <Card
                        style={{
                            padding: 20,
                            marginBottom: 20,
                        }}
                    >
                        <div>
                            <Typography style={{fontWeight: 'bold', fontSize: 24}}>Cashflow</Typography>
                        </div>
                        <Grid container spacing={3}>
                            {parameters.solveForIUL.value !== 'Cashflow' && (
                                <Grid item xs={6}>
                                    <SwitchCustom
                                        param={{
                                            name: 'disbursement',
                                            label: 'Income',
                                            ...parameters.disbursement,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                            )}
                            {parameters.disbursement.value && (
                                <>
                                    <Grid item xs={6}>
                                        <SelectInputElement
                                            param={{
                                                label: 'Income Option',
                                                name: 'incomeOption',
                                                options: fieldOptions.incomeOption.options,
                                                optionValues: fieldOptions.incomeOption.optionValues,
                                                ...parameters.incomeOption,
                                            }}
                                            required={''}
                                            valueChanged={valueChanged}
                                            formType={formType}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextBox
                                            id={'incomeStartAge'}
                                            label={'Income Start Age'}
                                            value={parameters.incomeStartAge?.value}
                                            onChange={(value) =>
                                                valueChanged(parameters.incomeStartAge, value, formType)
                                            }
                                            type="number"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextBox
                                            id={'incomeEndAge'}
                                            label={'Income End Age'}
                                            value={parameters.incomeEndAge?.value}
                                            onChange={(value) => valueChanged(parameters.incomeEndAge, value, formType)}
                                            type="number"
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextBox
                                            id={'annualIncomeAmount'}
                                            label={'Annual Income Amount'}
                                            value={parameters.annualIncomeAmount?.value}
                                            onChange={(value) =>
                                                valueChanged(parameters.annualIncomeAmount, value, formType)
                                            }
                                            InputProps={{
                                                inputComponent: NumberFormatCustom,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <SwitchCustom
                                            param={{
                                                name: 'overloanProtection',
                                                label: 'Overloan Protection',
                                                ...parameters.overloanProtection,
                                            }}
                                            required={''}
                                            valueChanged={valueChanged}
                                            formType={formType}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Card>
                )}
                {parameters.ameritas.value && (
                    <Card
                        style={{
                            padding: 20,
                            marginBottom: 20,
                        }}
                    >
                        <div>
                            <Typography style={{fontWeight: 'bold', fontSize: 24}}>Fund selection</Typography>
                        </div>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'fixedInterest'}
                                    label={'Fixed interest %'}
                                    value={parameters.fixedInterest?.value}
                                    onChange={(value) => valueChanged(parameters.fixedInterest, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'sp1yr'}
                                    label={'S&P 500 1-yr'}
                                    value={parameters.sp1yr?.value}
                                    onChange={(value) => valueChanged(parameters.sp1yr, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'spAdjust'}
                                    label={'Capped S&P 500 adjust'}
                                    value={parameters.spAdjust?.value}
                                    onChange={(value) => valueChanged(parameters.spAdjust, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'sp2yr'}
                                    label={'Capped S&P 500 2-yr'}
                                    value={parameters.sp2yr?.value}
                                    onChange={(value) => valueChanged(parameters.sp2yr, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'russell'}
                                    label={'Capped Russell 2000'}
                                    value={parameters.russell?.value}
                                    onChange={(value) => valueChanged(parameters.russell, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'bnp1yr'}
                                    label={'Uncapped BNP MMA 1-yr'}
                                    value={parameters.bnp1yr?.value}
                                    onChange={(value) => valueChanged(parameters.bnp1yr, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextBox
                                    id={'bnp2yr'}
                                    label={'Uncapped BNP MMA 2-yr'}
                                    value={parameters.bnp2yr?.value}
                                    onChange={(value) => valueChanged(parameters.bnp2yr, value, formType)}
                                    type="number"
                                />
                            </Grid>
                            {totalFundPercent !== 100 && (
                                <Grid item xs={6}>
                                    <div style={{position: 'relative'}}>
                                        <MuiAlert elevation={6} variant="filled" severity="warning">
                                            Percentages don't add up to 100%
                                        </MuiAlert>
                                    </div>
                                </Grid>
                            )}
                        </Grid>
                    </Card>
                )}
                <Card
                    style={{
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div>
                        <Typography style={{fontWeight: 'bold', fontSize: 24}}>Policy rider</Typography>
                    </div>
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <SwitchCustom
                                param={{
                                    name: 'acceleratedBenefit',
                                    label: 'Accelerated Death Benefit',
                                    ...parameters.acceleratedBenefit,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                            />
                        </Grid>
                        {parameters.ameritas.value && (
                            <>
                                <Grid item xs={6}>
                                    <SwitchCustom
                                        param={{
                                            name: 'accidentalBenefit',
                                            label: 'Accidental Death Benefit',
                                            ...parameters.accidentalBenefit,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                                {parameters.accidentalBenefit.value && (
                                    <>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'accidentalBenefitAmount'}
                                                label={'Accidental Death Benefit Amount'}
                                                value={parameters.accidentalBenefitAmount?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.accidentalBenefitAmount, value, undefined)
                                                }
                                                InputProps={{
                                                    inputComponent: NumberFormatCustom,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Accidental Benefit Rating',
                                                    name: 'accidentalBenefitRating',
                                                    options: fieldOptions.accidentalBenefitRating.options,
                                                    optionValues: fieldOptions.accidentalBenefitRating.optionValues,
                                                    ...parameters.accidentalBenefitRating,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                                formType={formType}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'accidentalBenefitToAge'}
                                                label={'Accidental Benefit To Age'}
                                                value={parameters.accidentalBenefitToAge?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.accidentalBenefitToAge, value, formType)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={6}>
                                    <SwitchCustom
                                        param={{
                                            name: 'childrensRider',
                                            label: "Children's Rider",
                                            ...parameters.childrensRider,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                                {parameters.childrensRider.value && (
                                    <>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'childrensRiderAmount'}
                                                label={"Children's Rider Amount"}
                                                value={parameters.childrensRiderAmount?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.childrensRiderAmount, value, undefined)
                                                }
                                                InputProps={{
                                                    inputComponent: NumberFormatCustom,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'childrensRiderToAge'}
                                                label={"Children's Rider To Age"}
                                                value={parameters.childrensRiderToAge?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.childrensRiderToAge, value, formType)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={6}>
                                    <SwitchCustom
                                        param={{
                                            name: 'lifetimeIncomeRider',
                                            label: 'Lifetime Income Rider',
                                            ...parameters.lifetimeIncomeRider,
                                        }}
                                        required={''}
                                        valueChanged={valueChanged}
                                        formType={formType}
                                    />
                                </Grid>
                                {parameters.lifetimeIncomeRider.value && (
                                    <>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Lifetime Income Type',
                                                    name: 'lifetimeIncomeType',
                                                    options: fieldOptions.lifetimeIncomeType.options,
                                                    optionValues: fieldOptions.lifetimeIncomeType.optionValues,
                                                    ...parameters.lifetimeIncomeType,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                                formType={formType}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'lifetimeIncomeStartAge'}
                                                label={'Lifetime Income Start Age'}
                                                value={parameters.lifetimeIncomeStartAge?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.lifetimeIncomeStartAge, value, formType)
                                                }
                                                type="number"
                                            />
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                        <Grid item xs={6}>
                            <SwitchCustom
                                param={{
                                    name: 'DeathBenefitProtection',
                                    label: 'Death Benefit Protection',
                                    ...parameters.DeathBenefitProtection,
                                }}
                                required={''}
                                valueChanged={valueChanged}
                                formType={formType}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </>
        );
    },
);

export default IULTab;
